/**
 * Pure row helpers for the Aggregate Stats locations table (issue #523).
 *
 * CommonJS + pure so it runs under `node --test`; the Redux wiring lives in
 * store/slices/stats.js, which imports from here.
 *
 * Every column — including the Name/Email account pair — is read straight off
 * the server-materialized `user_stats/{uid}/locations` row. There is no
 * client-side join: `stats()` resolves each location's owning account and
 * writes `accountName`/`accountEmail` into the row alongside the sales
 * aggregates.
 */

/**
 * Blank-aware sort value. A cell is blank when it is null, undefined, or a
 * whitespace-only string — for the account columns that means the location's
 * `users` map is empty or the account has no email on file.
 */
const isBlankCell = (value) =>
  value === null ||
  value === undefined ||
  (typeof value === "string" && value.trim() === "");

const sortValue = (value) => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    // Numeric columns arrive as numbers, but a numeric string must still sort
    // numerically — this is what the original comparator's isNaN() check did.
    if (trimmed !== "" && !isNaN(Number(trimmed))) return Number(trimmed);
    return trimmed.toLowerCase();
  }
  return value;
};

/**
 * Compare two rows on `key`, grouping blanks together at the END in BOTH sort
 * directions.
 *
 * The previous comparator coerced with `!isNaN(v) ? Number(v) : v`, and
 * `isNaN("")` is false — so an empty cell became the number 0. Comparing 0
 * against a name is false in both directions, so the comparator returned 0 and
 * declared the rows equal, scattering blank cells unpredictably through every
 * page instead of collecting them. A missing value is not alphabetically
 * before "A" nor after "Z", so it belongs in one predictable group rather than
 * at whichever end the direction implies.
 */
const compareLocationRows = (key, orderBy, a, b) => {
  const aBlank = isBlankCell(a?.[key]);
  const bBlank = isBlankCell(b?.[key]);
  if (aBlank || bBlank) {
    if (aBlank && bBlank) return 0;
    return aBlank ? 1 : -1;
  }

  const aVal = sortValue(a[key]);
  const bVal = sortValue(b[key]);
  if (aVal < bVal) return orderBy === "ascending" ? -1 : 1;
  if (aVal > bVal) return orderBy === "ascending" ? 1 : -1;
  return 0;
};

const sortLocationRows = (key, orderBy, rows) =>
  [...rows].sort((a, b) => compareLocationRows(key, orderBy, a, b));

module.exports = {
  compareLocationRows,
  isBlankCell,
  sortLocationRows,
};
